import { useState, useCallback, useEffect, useRef } from 'react'
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js'
import { getPriceRange, calculateTotalPriceRange, formatPriceRange, getRakutenSearchUrlForFurniture } from '../utils/priceCalculator'

// カテゴリ別のアイコン
// カテゴリ別のアイコン定義を削除（item.iconを使用）

export default function ResultModal({ isOpen, onClose, furnitureList, canvasRef, sceneRef, furnitureGroupRef }) {
    const [showEstimate, setShowEstimate] = useState(false)
    const [isCapturing, setIsCapturing] = useState(false)
    const [previewImage, setPreviewImage] = useState(null)

    // モーダルが開かれるたびに見積もり表示状態をリセットし、プレビュー画像を取得
    useEffect(() => {
        if (isOpen) {
            setShowEstimate(false)
            // プレビュー画像を取得
            capturePreview()
        } else {
            setPreviewImage(null)
        }
    }, [isOpen])

    // 見積もりが表示されたら自動スクロール
    const estimateRef = useRef(null)
    useEffect(() => {
        if (showEstimate && estimateRef.current) {
            // 少し待ってからスクロール（レンダリング待ち）
            setTimeout(() => {
                estimateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 100)
        }
    }, [showEstimate])

    // プレビュー画像をキャプチャ
    const capturePreview = useCallback(() => {
        if (!canvasRef?.current) return

        try {
            const canvas = canvasRef.current.querySelector('canvas')
            if (canvas) {
                const dataUrl = canvas.toDataURL('image/png')
                setPreviewImage(dataUrl)
            }
        } catch (error) {
            console.error('プレビュー取得エラー:', error)
        }
    }, [canvasRef])

    // 画像保存機能
    const handleSaveImage = useCallback(async () => {
        if (!canvasRef?.current) {
            alert('キャンバスが見つかりません')
            return
        }

        setIsCapturing(true)

        // 少し待ってモーダルが非表示になるのを確認
        await new Promise(resolve => setTimeout(resolve, 100))

        try {
            const canvas = canvasRef.current.querySelector('canvas')
            if (canvas) {
                // Canvasの内容をBlobとして取得
                canvas.toBlob((blob) => {
                    if (blob) {
                        const url = URL.createObjectURL(blob)
                        const link = document.createElement('a')
                        link.href = url
                        link.download = `furniture-layout-${Date.now()}.png`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        URL.revokeObjectURL(url)
                    }
                    setIsCapturing(false)
                }, 'image/png')
            } else {
                setIsCapturing(false)
                alert('キャンバスが見つかりません')
            }
        } catch (error) {
            console.error('画像保存エラー:', error)
            setIsCapturing(false)
            alert('画像の保存に失敗しました')
        }
    }, [canvasRef])

    // 3D保存機能（GLB形式）- 家具グループのみをエクスポート
    const handleSave3D = useCallback(async () => {
        // 家具グループを優先、なければシーン全体をエクスポート
        const exportTarget = furnitureGroupRef?.current || sceneRef?.current

        if (!exportTarget) {
            alert('3Dシーンが見つかりません')
            return
        }

        try {
            const exporter = new GLTFExporter()
            exporter.parse(
                exportTarget,
                (gltf) => {
                    const blob = new Blob([gltf], { type: 'application/octet-stream' })
                    const url = URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = `furniture-layout-${Date.now()}.glb`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(url)
                },
                (error) => {
                    console.error('3Dエクスポートエラー:', error)
                    alert('3Dデータの保存に失敗しました')
                },
                { binary: true }
            )
        } catch (error) {
            console.error('3D保存エラー:', error)
            alert('3Dデータの保存に失敗しました')
        }
    }, [furnitureGroupRef, sceneRef])

    // Xでシェア機能
    const handleShareX = useCallback(() => {
        const text = encodeURIComponent(`Furniture Checkerで家具レイアウトを作成しました！🛋️\nhttps://furniture-checker.pages.dev/\n#FurnitureChecker`)
        const url = `https://twitter.com/intent/tweet?text=${text}`
        window.open(url, '_blank')
    }, [])

    // 合計価格レンジを計算
    const totalPriceRange = calculateTotalPriceRange(furnitureList)

    // モーダルが閉じているときは何も表示しない
    if (!isOpen) return null

    return (
        <>
            {/* オーバーレイ - キャプチャ中は非表示 */}
            <div
                className={`fixed inset-0 bg-black/70 z-50 flex items-center justify-center ${isCapturing ? 'hidden' : ''}`}
                onClick={onClose}
            >
                {/* モーダル本体 - Flexboxレイアウトで高さ調整 */}
                <div
                    className="glass-effect rounded-3xl p-4 md:p-6 max-w-4xl w-full mx-4 transform transition-all duration-300 max-h-[90vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* タイトル - 共通ヘッダーとして配置 */}
                    <div className="text-center mb-6">
                        <div className="text-4xl mb-2">🎉</div>
                        <h2 className="text-xl font-bold text-white">配置完了</h2>
                        <p className="text-gray-400 text-sm mt-1">
                            {furnitureList.length}個の家具を配置しました
                        </p>
                    </div>

                    {/* コンテンツエリア - 残りの高さを埋める */}
                    {/* コンテンツエリア - 残りの高さを埋める */}
                    <div className="flex-1 min-h-0 flex flex-col lg:flex-row-reverse gap-6 items-center overflow-y-auto custom-scrollbar lg:overflow-visible">
                        {/* プレビュー画像 - 可能な限り広げる */}
                        <div className="w-full lg:w-1/2 flex-1 min-h-[200px] lg:min-h-0 relative flex flex-col justify-center bg-black/40 rounded-2xl overflow-hidden mt-4 lg:mt-0">
                            {previewImage ? (
                                <img
                                    src={previewImage}
                                    alt="レイアウトプレビュー"
                                    className="absolute inset-0 lg:static w-full h-full lg:h-auto object-contain"
                                />
                            ) : (
                                <div className="absolute inset-0 lg:static flex items-center justify-center text-gray-500 text-sm lg:p-10">
                                    プレビューを読み込み中...
                                </div>
                            )}
                        </div>

                        {/* アクションとリスト */}
                        <div className="w-full lg:w-1/2 flex flex-col flex-shrink-0">

                            {/* メイン機能ボタン */}
                            <div className="grid grid-cols-3 gap-2 mb-4 w-full">
                                <button
                                    onClick={handleSaveImage}
                                    className="flex flex-col items-center gap-1 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200"
                                >
                                    <span className="text-xl">📷</span>
                                    <span className="text-white text-xs font-medium">画像保存</span>
                                </button>
                                <button
                                    onClick={handleSave3D}
                                    className="flex flex-col items-center gap-1 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200"
                                >
                                    <span className="text-xl">📦</span>
                                    <span className="text-white text-xs font-medium">3D保存</span>
                                </button>
                                <button
                                    onClick={handleShareX}
                                    className="flex flex-col items-center gap-1 p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-200"
                                >
                                    <span className="text-xl font-bold text-white">𝕏</span>
                                    <span className="text-white text-xs font-medium">シェア</span>
                                </button>
                            </div>

                            {/* 見積もり表示ボタン */}
                            {!showEstimate && (
                                <button
                                    onClick={() => setShowEstimate(true)}
                                    className="w-full py-2 px-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 text-amber-400 font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                                >
                                    <span>💰</span>
                                    <span>この部屋の概算見積を見る</span>
                                </button>
                            )}

                            {/* 見積もり詳細 */}
                            {showEstimate && (
                                <div ref={estimateRef} className="animate-fadeIn flex-1">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-white font-semibold text-sm">概算見積</h3>
                                        <button
                                            onClick={() => setShowEstimate(false)}
                                            className="text-gray-400 hover:text-white text-xs flex items-center gap-1"
                                        >
                                            <span>×</span>
                                            <span>見積を閉じる</span>
                                        </button>
                                    </div>

                                    {/* 家具リスト */}
                                    <div className="bg-black/30 rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
                                        {furnitureList.map((item) => {
                                            const priceRange = getPriceRange(item.category)
                                            const icon = item.icon || '📦'
                                            const rakutenUrl = getRakutenSearchUrlForFurniture(item)

                                            return (
                                                <div key={item.id} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                                                    {/* 列1: 家具名 */}
                                                    <div className="flex items-center gap-2 min-w-0 bg-white/5 rounded-lg px-2 py-1">
                                                        <span className="text-base">{icon}</span>
                                                        <span className="text-white text-xs truncate">{item.name}</span>
                                                    </div>
                                                    {/* 列2: 金額（カスタムは非表示だがスペース維持） */}
                                                    <div className={`flex items-center text-gray-300 text-xs ${item.category === 'custom' ? 'invisible' : ''}`}>
                                                        <span>¥</span>
                                                        <span className="w-12 text-right">{priceRange.min.toLocaleString()}</span>
                                                        <span className="text-gray-400 mx-1">〜</span>
                                                        <span>¥</span>
                                                        <span className="w-12 text-right">{priceRange.max.toLocaleString()}</span>
                                                    </div>
                                                    {/* 列3: 楽天ボタン（カスタムは非表示） */}
                                                    {rakutenUrl ? (
                                                        <a
                                                            href={rakutenUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors whitespace-nowrap text-center"
                                                        >
                                                            楽天で探す
                                                        </a>
                                                    ) : (
                                                        <span className="px-2 py-1 text-gray-500 text-xs text-center" style={{ minWidth: '64px' }}>-</span>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>

                                    {/* アフィリエイト注記 */}
                                    <p className="text-xs text-gray-500 text-right mt-1">
                                        ※本ページはプロモーションが含まれています
                                    </p>

                                    {/* 合計金額 */}
                                    <div className="mt-3 pt-3 border-t border-gray-600">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-gray-300 text-sm font-medium whitespace-nowrap">予算目安</span>
                                            <span className="text-sm font-bold text-white whitespace-nowrap">
                                                約 ¥{totalPriceRange.totalMin.toLocaleString()} 〜 ¥{totalPriceRange.totalMax.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 閉じるボタン */}
                            <button
                                onClick={onClose}
                                className="w-full mt-4 py-2 px-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-200 text-sm"
                            >
                                編集に戻る
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
